import { useCallback, useEffect, useRef } from 'react';
import { createCloseWatcher } from './useCloseWatcher';

// 모바일 웹/PWA에서 안드로이드 하드웨어 뒤로가기를 화면 단계(채팅방 → 대화방 목록 → 홈)와
// 동기화하는 훅. UI state가 진실의 원천이고 history는 파생물이다:
// 기존 버튼/탭바는 그대로 setState만 하고, 이 훅이 depth 변화를 관찰해
// pushState / history.go 로 entry 개수를 맞춘다. popstate는 멱등 applyDepth 호출뿐이라
// forward·iOS 스와이프 백에도 안전하고, entry가 어긋나도 최악이 no-op 뒤로가기 한 번이다.
//
// depth 0 = 홈 (base entry, push 안 함 → 홈에서 뒤로가기는 앱 종료로 자연 진행)
// depth 1 = 대화방 목록
// depth 2 = 채팅방 전체화면
// entry 태그: history.state.inaNav = depth (객체 병합 유지 — 다른 코드의 state 보존)
//
// ⚠ history만으로는 안드로이드에서 부족하다. Chrome은 사용자 입력(탭·키) 없이 pushState로 쌓인
// 항목을 뒤로가기 버튼에서 건너뛰고(History Manipulation Intervention), 갈 곳이 없으면 앱 창을 닫는다.
// 웹푸시 알림으로 막 뜬 페이지가 정확히 그 상태라 "알림 → 채팅방 → 뒤로가기 = 앱 종료"가 났다.
// 그래서 CloseWatcher(Chrome 120+)를 1순위로 둔다([4]) — 뒤로가기를 history 이동보다 먼저 받고
// 입력 없이도 만들 수 있다. 받은 뒤엔 UI를 한 단계 얕게 만들 뿐이고, history 정산은 헤더 뒤로가기
// 버튼과 같은 [2] 경로(history.go — 이 정책의 대상이 아니다)가 맡는다.
// iOS·미지원 브라우저는 CloseWatcher가 없어 기존 popstate 경로([3]) 그대로다.

interface UseMobileBackNavOptions {
  enabled: boolean;
  depth: number;
  applyDepth: (depth: number) => void;
}

const readHistoryDepth = (state: unknown): number => {
  const d = (state as { inaNav?: unknown } | null)?.inaNav;
  return typeof d === 'number' ? Math.max(0, Math.min(2, d)) : 0;
};

export function useMobileBackNav({ enabled, depth, applyDepth }: UseMobileBackNavOptions) {
  const applyRef = useRef(applyDepth);
  useEffect(() => { applyRef.current = applyDepth; });
  const enabledRef = useRef(enabled);
  // popstate 처리로 UI가 얕아진 렌더 사이클에서 정산 effect가 pop을 되돌리지 않게 차단
  const popInProgressRef = useRef(false);

  // [1] 활성화/비활성화 전환
  useEffect(() => {
    enabledRef.current = enabled;
    if (enabled) {
      if (typeof (window.history.state as { inaNav?: unknown } | null)?.inaNav !== 'number') {
        window.history.replaceState({ ...(window.history.state || {}), inaNav: 0 }, '');
      }
    } else {
      // 데스크톱 전환·로그아웃: 잔여 entry 되감기 (이때의 popstate는 enabledRef=false로 무시됨)
      const d = readHistoryDepth(window.history.state);
      if (d > 0) window.history.go(-d);
    }
  }, [enabled]);

  // [2] UI depth 변화 → history 정산
  useEffect(() => {
    if (!enabled || popInProgressRef.current) return;
    const hist = readHistoryDepth(window.history.state);
    if (depth > hist) {
      // 홈에서 알림 클릭 등 0→2 점프여도 중간 entry를 채워 뒤로가기가 단계적으로 풀리게 한다
      for (let d = hist + 1; d <= depth; d++) {
        window.history.pushState({ inaNav: d }, '');
      }
    } else if (depth < hist) {
      // 헤더 뒤로가기 버튼/탭바로 UI가 먼저 얕아진 경우 entry 회수
      // → 도착할 popstate의 applyDepth는 현재 state와 동일해 no-op
      window.history.go(depth - hist);
    }
  }, [enabled, depth]);

  // [3] 하드웨어 뒤로가기 / iOS 스와이프 / forward
  const onPopState = useCallback((e: PopStateEvent) => {
    if (!enabledRef.current) return;
    popInProgressRef.current = true;
    applyRef.current(readHistoryDepth(e.state));
    // React 커밋 이후 정산 effect가 이 pop을 push로 오인하지 않도록 다음 프레임에 해제
    requestAnimationFrame(() => { popInProgressRef.current = false; });
  }, []);

  useEffect(() => {
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [onPopState]);

  // [4] 안드로이드 뒤로가기 = 닫기 요청(CloseWatcher). 단계마다 1개만 유지한다 — depth가 바뀌면
  // cleanup이 이전 것을 없애고 새로 만든다. 메뉴·모달의 watcher(useCloseWatcher)가 나중에 생기면
  // 그쪽이 먼저 닫힌다. 홈(depth 0)에는 두지 않아 뒤로가기 = 앱 종료가 유지된다.
  useEffect(() => {
    if (!enabled || depth <= 0) return;
    const watcher = createCloseWatcher(() => applyRef.current(depth - 1));
    return () => watcher?.destroy();
  }, [enabled, depth]);
}
